import { SelectChangeEvent } from '@mui/material';
import { ChangeEvent, useContext, useEffect, useMemo, useState } from 'react';
import { Item, PrimaryCategoryStockCount, StockCheckSchedule, StockCheckScheduleCategory, StockCheckScheduleDetail, StockCheckScheduleItem, Store2 } from '../../../app/services/api/generated';
import { UserContext } from '../../../components/shared/useUser';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { InternalStockCheckSchedule } from '../interfaces/StockInterfaces';
import { useNavigate, useParams, useBlocker } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useLogError from '../../../hooks/useLogError';
import { ConfigurationName } from '../../../enums/ConfigurationName';
import { StockCountScheduleType } from '../enums/StockScheduleEnums';

export const useScheduleCreate = () => {
  //use hooks
  const { t } = useTranslation('createStockCountSchedule');
  const { selectedStore, selectedFranchise, hasPermissionTo } = useContext(UserContext);
  const { getConfiguration, generatestockchecks, getStockCheckScheduleByNumber, stockcheckschedulePOST } = useNswagClient();
  const navigate = useNavigate();
  const { logError } = useLogError();
  //consts
  const today = new Date().toISOString().split('T')[0];

  //modal open state
  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false);
  const [isItemsModalOpen, setIsItemsModalOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCategoryDeleteModalOpen, setIsCategoryDeleteModalOpen] = useState(false);
  const [isScheduleDeleteModalOpen, setIsScheduleDeleteModalOpen] = useState(false);

  //snackbar modal state
  const [snackBarDuration, setSnackBarDuration] = useState<number | null>(6000);
  const [isSnackBarOpen, setIsSnackBarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackBarSeverity, setSnackBarSeverity] = useState<'error' | 'warning' | 'info' | 'success'>('error');
  const [isDayOfMonthSnackBarOpen, setIsDayOfMonthSnackBarOpen] = useState(false);

  //other state
  const [categoryData, setCategoryData] = useState<StockCheckScheduleCategory[]>([]);

  const [itemsData, setItemsData] = useState<StockCheckScheduleItem[]>([]);
  const [schedules, setSchedules] = useState<InternalStockCheckSchedule[]>([]);
  const [stores, setStores] = useState<Store2[]>([]);
  const [allStores, setAllStores] = useState<Store2[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasCreated, setHasCreated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  //row delete ids
  const [categoryNameToDelete, setCategoryNameToDelete] = useState<string>();
  const [itemNumberToDelete, setItemNumberToDelete] = useState<string>();
  const [scheduleIds, setScheduleIds] = useState<number[]>();
  const [scheduleToDeleteIndex, setScheduleToDeleteIndex] = useState<number | undefined>();
  const [deleteAllSchedulesFlag, setDeleteAllSchedulesFlag] = useState<boolean>(false);
  // 'active' when confirming the top-level Active switch, a schedule row index when confirming
  // an individual schedule's Active switch, or null when the modal is closed
  const [deactivateTarget, setDeactivateTarget] = useState<'active' | number | null>(null);
  // confirmation shown on Save/Create when the top-level Active toggle or any schedule row is inactive
  const [isSaveConfirmOpen, setIsSaveConfirmOpen] = useState(false);

  //main box values
  const [name, setName] = useState('');
  const [allUsers, setAllUsers] = useState<string[]>([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [approvalValue, setApprovalValue] = useState<number | undefined>();
  const [selectedTime, setSelectedTime] = useState<number>();
  const [isTimeLocked, setIsTimeLocked] = useState<boolean>(false);
  const [isSmartCount, setIsSmartCount] = useState<boolean>(false);
  const [sliderValue, setSliderValue] = useState<number>(50);
  const [isActive, setIsActive] = useState(false);
  const [currencySymbol, setCurrencySymbol] = useState('');
  const [hasChanged, setHasChanged] = useState(false);
  const [hasRightToEdit, setHasRightToEdit] = useState(false);

  const { id } = useParams();

  const isCreating = Number(id) <= 0 || id === undefined;

  const blocker = useBlocker(() => hasChanged && !hasCreated);

  const handleLeavePageModalClose = () => {
    if (blocker?.reset) {
      blocker.reset();
    }
  };

  const handleLeavePageModalConfirm = () => {
    if (name && (schedules.length > 0) && hasRightToEdit) {
      submitStock();
    }
    if (blocker?.proceed) {
      blocker.proceed();
    }
  };

  const handleLeavePageModalCancel = () => {
    if (blocker?.proceed) {
      blocker.proceed();
    }
  };

  useEffect(() => {
    loadData();
    getCurrencyCode();
    if (hasPermissionTo(['StockCountScheduleWrite'])) {
      setHasRightToEdit(true);
    }
  }, []);

  const getCurrencyCode = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getConfiguration(ConfigurationName.CurrencyCode, selectedStore?.storeNumber ?? '', selectedStore?.franchiseName ?? '').then((res: any) => {
      const code = new Intl.NumberFormat('en', { style: 'currency', currency: res.data?.value }).formatToParts(1)[0].value;
      setCurrencySymbol(code);
    });
  };

  // group schedules by multiStoresGroupId
  const groupSchedules = (schedules: StockCheckSchedule[]): InternalStockCheckSchedule[] => {
    const schedulesWithGroup: StockCheckSchedule[] = [];
    const schedulesWithoutGroup: StockCheckSchedule[] = [];

    schedules.forEach((item) => {
      if (item?.multiStoresGroupId) {
        schedulesWithGroup.push(item);
      } else {
        schedulesWithoutGroup.push(item);
      }
    });

    const schedulesByMultiStoreGroup = schedulesWithGroup.reduce((acc, item) => {
      const groupId = item.multiStoresGroupId!;
      acc[groupId] = acc[groupId] || [];
      acc[groupId].push(item);
      return acc;
    }, {} as { [key: string]: StockCheckSchedule[] });

    const groupedSchedules: InternalStockCheckSchedule[] = Object.values(schedulesByMultiStoreGroup).map((groups) => ({
      ids: groups.map((item: StockCheckSchedule) => item.id) as number[],
      stockCheckScheduleDetailId: groups[0].stockCheckScheduleDetailId,
      storeNumbers: groups.map((item: StockCheckSchedule) => item.storeNumber) as string[],
      isActive: groups[0].isActive,
      generationDayOffset: groups[0].generationDayOffset,
      startDate: groups[0].startDate,
      recurrenceFrequency: groups[0].recurrenceFrequency,
      recurrenceInterval: groups[0].recurrenceInterval,
      scheduleType: groups[0].scheduleType,
      scheduledDayOfMonth: groups[0].scheduledDayOfMonth,
      stockCheckLastCreated: groups[0].stockCheckLastCreated,
      allStoresSelected: groups[0].allStoresSelected,
      multiStoresGroupId: groups[0].multiStoresGroupId,
    }));

    // Treat schedules without a multiStoresGroupId as individual single-store groups
    const ungroupedSchedules: InternalStockCheckSchedule[] = schedulesWithoutGroup.map((item) => ({
      ids: [item.id] as number[],
      stockCheckScheduleDetailId: item.stockCheckScheduleDetailId,
      storeNumbers: [item.storeNumber] as string[],
      isActive: item.isActive,
      generationDayOffset: item.generationDayOffset,
      startDate: item.startDate,
      recurrenceFrequency: item.recurrenceFrequency,
      recurrenceInterval: item.recurrenceInterval,
      scheduleType: item.scheduleType,
      scheduledDayOfMonth: item.scheduledDayOfMonth,
      stockCheckLastCreated: item.stockCheckLastCreated,
      allStoresSelected: item.allStoresSelected,
      multiStoresGroupId: item.multiStoresGroupId,
    }));

    return [...groupedSchedules, ...ungroupedSchedules];
  };

  const getStoresForSelectedFranchise = (allStores: Store2[]): Store2[] => {
    const franchiseId = selectedFranchise?.franchiseId;
    const filtered = !franchiseId
      ? allStores
      : allStores.filter(store => {
        const isWholeFranchiseOption = !store.storeNumber;
        return isWholeFranchiseOption || store.franchiseId === franchiseId;
      });

    const seen = new Map<string, Store2>();
    for (const store of filtered) {
      const key = store.storeNumber ?? '';
      if (!seen.has(key)) {
        seen.set(key, store);
      }
    }
    return Array.from(seen.values());
  };

  useEffect(() => {
    setStores(getStoresForSelectedFranchise(allStores));
  }, [allStores, selectedFranchise?.franchiseId]);

  const loadData = () => {
    setIsLoading(true);
    getStockCheckScheduleByNumber(selectedStore?.storeNumber ?? '', Number(id ?? 0)).then((result) => {
      if (result.data) {
        setAllStores(result.data.stores ?? []);
        const StockCheckScheduleDetails = result.data;

        setCategoryData(StockCheckScheduleDetails.stockCheckSchedule?.categories ?? []);

        setItemsData(StockCheckScheduleDetails.stockCheckSchedule?.items ?? []);

        setSchedules(groupSchedules(StockCheckScheduleDetails.stockCheckSchedule?.schedules ?? []));

        setAllUsers(StockCheckScheduleDetails.approverEmails ?? []);
        setName(StockCheckScheduleDetails.stockCheckSchedule?.name ?? '');
        setSelectedUser(StockCheckScheduleDetails.stockCheckSchedule?.approver ?? '');
        setApprovalValue(StockCheckScheduleDetails.stockCheckSchedule?.toleranceValue);
        setSelectedTime(StockCheckScheduleDetails.stockCheckSchedule?.lockTillHour);
        setIsTimeLocked(StockCheckScheduleDetails.stockCheckSchedule?.lockTillCountDay ?? false);
        setIsSmartCount(StockCheckScheduleDetails.stockCheckSchedule?.isSmartCount ?? false);
        setSliderValue(StockCheckScheduleDetails.stockCheckSchedule?.tolerancePercentage ?? 0);
        setIsActive(StockCheckScheduleDetails.stockCheckSchedule?.isActive ?? false);
      }
    })
      .catch(e => {
        logError(e);
        setSnackBarProps('error', `Error loading schedules: ${e}. please try again`);
      })
      .finally(() => setIsLoading(false));
  };

  const handleActiveChange = (event: SelectChangeEvent) => {
    if (isActive) {
      // turning off - confirm first, since this stops stock count generation
      setDeactivateTarget('active');
    } else {
      setIsActive(true);
      setHasChanged(true);
    }
  };

  const handleScheduleActiveChange = (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSchedules = schedules.map((a, i) => {
        const returnValue = { ...a };
        if (index === i) {
          returnValue.isActive = true;
        }
        return returnValue;
      });
      setSchedules(newSchedules);
      setHasChanged(true);
    } else {
      // turning off - confirm first, since this stops this schedule's stock counts
      setDeactivateTarget(index);
    }
  };

  const confirmDeactivate = () => {
    if (deactivateTarget === 'active') {
      setIsActive(false);
      setHasChanged(true);
    } else if (typeof deactivateTarget === 'number') {
      const newSchedules = schedules.map((a, i) => {
        const returnValue = { ...a };
        if (deactivateTarget === i) {
          returnValue.isActive = false;
        }
        return returnValue;
      });
      setSchedules(newSchedules);
      setHasChanged(true);
    }
    setDeactivateTarget(null);
  };

  // save/create is guarded by the same deactivation warning: if the schedule (or any of its
  // individual schedule rows) is currently inactive, confirm before creating or updating it
  const hasInactiveItems = !isActive || schedules.some(schedule => !schedule.isActive);

  const handleSaveClick = () => {
    if (hasInactiveItems) {
      setIsSaveConfirmOpen(true);
    } else {
      submitStock();
    }
  };

  const confirmSave = () => {
    setIsSaveConfirmOpen(false);
    submitStock();
  };

  const handleIsTimeLockedChanged = (event: SelectChangeEvent) => {
    setIsTimeLocked(!isTimeLocked);
    setHasChanged(true);
  };

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    if (typeof newValue === 'number') {
      setSliderValue(newValue);
    }
    setHasChanged(true);
  };

  const handleCategories = (data: PrimaryCategoryStockCount[]) => {
    const convertedCategories = data.map(x => {
      return {
        primaryCategoryId: x.id,
        primaryCategoryName: x.primaryCategoryName,
        stockCheckScheduleDetailId: id ? Number(id) : 0,
      };
    });
    const categories = Array.from(new Set(categoryData.concat(convertedCategories)));
    setCategoryData(categories);
    setIsCategoriesModalOpen(false);
    setHasChanged(true);
  };

  const deleteCategories = () => {
    let data: PrimaryCategoryStockCount[] = [];
    if (categoryNameToDelete != undefined) {
      data = categoryData?.filter(item => item.primaryCategoryName !== categoryNameToDelete);
    }
    setCategoryData(data);
    handleClose();
    setHasChanged(true);
  };

  const deleteItems = () => {
    let data: Item[] = [];
    if (itemNumberToDelete != undefined) {
      data = itemsData?.filter(item => item.itemNumber !== itemNumberToDelete);
    }
    setItemsData(data);
    handleClose();
    setHasChanged(true);
  };

  // two schedules can generate a count for the same store on the same calendar date if they
  // share a store and their recurrence patterns coincide on a date. The generationDayOffset only
  // affects when the count is created (in advance of the count date), not the count date itself,
  // so it is deliberately excluded from this comparison.
  const schedulesShareStore = (a: InternalStockCheckSchedule, b: InternalStockCheckSchedule) => {
    const storesA = a.storeNumbers ?? [];
    const storesB = b.storeNumbers ?? [];
    // an empty string entry represents "whole franchise" and overlaps with any store selection
    if (storesA.includes('') || storesB.includes('')) {
      return true;
    }
    return storesA.some(store => storesB.includes(store));
  };

  // normalizes a date string (date-only "YYYY-MM-DD" or ISO-with-time) to a UTC midnight
  // timestamp so comparisons aren't affected by time-of-day/timezone components
  const toUtcDateOnly = (date?: string): number | undefined => {
    if (!date) {
      return undefined;
    }
    const dateOnly = date.split('T')[0];
    const parsed = new Date(`${dateOnly}T00:00:00Z`).getTime();
    return isNaN(parsed) ? undefined : parsed;
  };

  const isSameDate = (dateA?: string, dateB?: string): boolean => {
    const a = toUtcDateOnly(dateA);
    const b = toUtcDateOnly(dateB);
    return a !== undefined && a === b;
  };

  const daysBetween = (dateA?: string, dateB?: string): number | undefined => {
    const msPerDay = 24 * 60 * 60 * 1000;
    const a = toUtcDateOnly(dateA);
    const b = toUtcDateOnly(dateB);
    if (a === undefined || b === undefined) {
      return undefined;
    }
    return (b - a) / msPerDay;
  };

  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

  // a monthly day-of-month of 28-31 can fall on the last day of a given month (months vary
  // between 28 and 31 days), so it can collide with a LAST_DAY_OF_MONTH schedule
  const isEndOfMonthDay = (scheduledDayOfMonth?: number) => scheduledDayOfMonth !== undefined && scheduledDayOfMonth >= 28;

  const schedulesShareRecurrence = (a: InternalStockCheckSchedule, b: InternalStockCheckSchedule) => {
    if (a.scheduleType === StockCountScheduleType.LAST_DAY_OF_MONTH && b.scheduleType === StockCountScheduleType.MONTHLY_DATE) {
      return isEndOfMonthDay(b.scheduledDayOfMonth);
    }
    if (b.scheduleType === StockCountScheduleType.LAST_DAY_OF_MONTH && a.scheduleType === StockCountScheduleType.MONTHLY_DATE) {
      return isEndOfMonthDay(a.scheduledDayOfMonth);
    }

    if (a.scheduleType !== b.scheduleType) {
      return false;
    }

    switch (a.scheduleType) {
    case StockCountScheduleType.FREQUENCY_AND_INTERVAL: {
      const stepA = (a.recurrenceFrequency ?? 0) * (a.recurrenceInterval ?? 0);
      const stepB = (b.recurrenceFrequency ?? 0) * (b.recurrenceInterval ?? 0);
      const diff = daysBetween(a.startDate, b.startDate);
      if (!stepA || !stepB || diff === undefined) {
        return false;
      }
      // the two recurring sequences land on the same date at some point if their step sizes
      // share a common divisor that also divides the gap between their start dates
      return diff % gcd(stepA, stepB) === 0;
    }
    case StockCountScheduleType.MONTHLY_DATE:
      // recurs on the same day-of-month every month regardless of when each schedule started
      return a.scheduledDayOfMonth !== undefined && a.scheduledDayOfMonth === b.scheduledDayOfMonth;
    case StockCountScheduleType.LAST_DAY_OF_MONTH:
      // always lands on the same date every month regardless of start date
      return true;
    case StockCountScheduleType.ONE_TIME:
      // a single occurrence, so only a duplicate if it is on the same calendar date
      return isSameDate(a.startDate, b.startDate);
    default:
      return false;
    }
  };


  // indexes of schedule rows that would generate duplicate stock counts for a store on the same day
  const duplicateScheduleIndexes = useMemo(() => {
    const duplicates = new Set<number>();
    for (let i = 0; i < schedules.length; i++) {
      if (!schedules[i].isActive) {
        continue;
      }
      for (let j = i + 1; j < schedules.length; j++) {
        if (!schedules[j].isActive) {
          continue;
        }
        if (schedulesShareStore(schedules[i], schedules[j]) && schedulesShareRecurrence(schedules[i], schedules[j])) {
          duplicates.add(i);
          duplicates.add(j);
        }
      }
    }
    return duplicates;
  }, [schedules]);

  const hasDuplicateSchedules = duplicateScheduleIndexes.size > 0;

  const areArraysEqual = (first: number[], second: number[]) => {
    if (first.length !== second.length) {
      return false;
    }

    for (let i = 0; i < first.length; i++) {
      if (first[i] !== second[i]) {
        return false;
      }
    }

    return true;
  };

  const confirmDeleteSchedule = () => {
    let data: InternalStockCheckSchedule[] = [];

    if (deleteAllSchedulesFlag === true) {
      data = [];
    } else if (scheduleToDeleteIndex !== undefined) {
      // remove non existing schedule items
      data = schedules?.filter((item, index) => index !== scheduleToDeleteIndex);
    } else {
      // remove existing schedule items
      data = schedules?.filter(item => !areArraysEqual(item.ids ?? [], scheduleIds ?? []));
    }
    setSchedules(data);
    handleClose();
    setHasChanged(true);
  };

  const deleteCategoryRow = (nameToDelete?: string) => {
    setCategoryNameToDelete(nameToDelete);
    setIsCategoryDeleteModalOpen(true);
  };

  const deleteItemRow = (itemNumberToDelete?: string) => {
    setItemNumberToDelete(itemNumberToDelete);
    setIsDeleteModalOpen(true);
  };

  const deleteScheduleRow = (index?: number) => {
    if (index !== undefined) {
      const scheduleToDelete: InternalStockCheckSchedule = schedules[index];
      if (scheduleToDelete?.ids?.length !== 0) {
        // delete existing schedule row
        setScheduleIds(scheduleToDelete?.ids ?? []);
      } else {
        // delete non-existing schedule row
        setScheduleToDeleteIndex(index);
      }
    } else {
      // delete all schedule rows
      setDeleteAllSchedulesFlag(true);
    }

    setIsScheduleDeleteModalOpen(true);
  };

  const handleitems = (data: Item[]) => {
    const convertedData = data.filter(item => !itemsData.some(x => x.itemNumber === item.itemNumber)).map(item => {
      return {
        id: 0,
        stockCheckScheduleDetailId: undefined,
        itemNumber: item.itemNumber,
        description: item.description,
        category: item.primaryCategory,
        tolerancePercentage: 0,
        toleranceValue: 0,
      };
    });
    const items = Array.from(new Set(itemsData.concat(convertedData ?? [])));
    setItemsData(items);
    setHasChanged(true);
  };

  const handleClose = () => {
    setIsCategoriesModalOpen(false);
    setIsItemsModalOpen(false);
    setIsBulkModalOpen(false);
    setIsCategoryDeleteModalOpen(false);
    setIsDeleteModalOpen(false);
    setIsScheduleDeleteModalOpen(false);
    setDeleteAllSchedulesFlag(false);
    setScheduleToDeleteIndex(undefined);
    setDeactivateTarget(null);
    setIsSaveConfirmOpen(false);
  };

  const handleAddCategoriesClick = () => {
    setIsCategoriesModalOpen(true);
  };

  const handleAddItemsClick = () => {
    setIsItemsModalOpen(true);
  };

  const handleAddBulkItemClick = () => {
    setIsBulkModalOpen(true);
  };

  const submitStock = () => {
    if (isSubmitting || hasDuplicateSchedules) {
      return;
    }
    isCreating ? createStockSchedule() : updateStock();
  };

  const mapSchedules = (): StockCheckSchedule[] => {
    let multiStoresGroupId = 1;

    return schedules.flatMap((schedule: InternalStockCheckSchedule) => {
      multiStoresGroupId = schedule?.multiStoresGroupId ? schedule.multiStoresGroupId : multiStoresGroupId + 1;
      return (schedule.storeNumbers || []).map((store, index) => {
        const { storeNumbers, ...rest } = schedule;

        return {
          ...rest,
          id: schedule?.ids?.[index] ?? 0,
          storeNumber: store,
          multiStoresGroupId: schedule.multiStoresGroupId ? schedule.multiStoresGroupId : multiStoresGroupId + 1,
        };
      });
    });
  };

  const createStockSchedule = () => {
    const mappedSchedules: StockCheckSchedule[] = mapSchedules();

    const body: StockCheckScheduleDetail = {
      id: 0,
      name: name,
      franchise: selectedStore?.franchiseId,
      isActive: isActive,
      tolerancePercentage: sliderValue,
      toleranceValue: approvalValue,
      isSmartCount: isSmartCount,
      canMergeCount: true,
      lockTillCountDay: isTimeLocked,
      lockTillHour: selectedTime,
      updateSmartCountItems: false,
      approver: selectedUser,
      nextUpcomingCount: '',
      schedules: mappedSchedules,
      items: itemsData,
      categories: categoryData,
    };

    setSnackBarProps('info', 'Creating schedule');
    setHasCreated(true);
    setIsSubmitting(true);

    stockcheckschedulePOST(selectedStore?.storeNumber ?? '-1', body)
      .then(() => {
        setSnackBarProps('success', 'Created schedule');
        navigate('/stock-count-schedule');
      })
      .catch((error) => {
        setHasCreated(false);
        setSnackBarProps('error', t('errorMessage') + ' ' + error);
      })
      .finally(() => setIsSubmitting(false));
  };

  const updateStock = () => {
    const mappedSchedules: StockCheckSchedule[] = mapSchedules();

    const body: StockCheckScheduleDetail = {
      id: Number(id),
      name: name,
      franchise: selectedStore?.franchiseId,
      isActive: isActive,
      tolerancePercentage: sliderValue,
      toleranceValue: approvalValue,
      isSmartCount: isSmartCount,
      canMergeCount: true,
      lockTillCountDay: isTimeLocked,
      lockTillHour: selectedTime,
      updateSmartCountItems: false,
      approver: selectedUser,
      nextUpcomingCount: '',
      schedules: mappedSchedules,
      items: itemsData,
      categories: categoryData,
    };

    setSnackBarProps('info', 'Updating schedule');
    setIsSubmitting(true);
    stockcheckschedulePOST(selectedStore?.storeNumber ?? '-1', body)
      .then(() => {
        setSnackBarProps('success', 'Updated schedule');
        setHasChanged(false);
        loadData();
      })
      .catch((error) => {
        setSnackBarProps('error', t('errorMessage') + ' ' + error);
      })
      .finally(() => setIsSubmitting(false));
  };

  const generateStock = () => {
    setIsGenerating(true);
    setSnackBarProps('info', 'Generating schedules');
    generatestockchecks(selectedStore?.storeNumber ?? '-1', Number(id))
      .then((response) => {
        const success = response.success ?? 0;
        const fails = response.fails ?? 0;
        const queued = response.queued ?? false;
        const storeCount = response.storeCount ?? 0;

        if (queued) {
          setSnackBarProps('info', `Stock counts for ${storeCount} stores will be checked and generated. This may take a few minutes before they appear.`);
        }
        else if (success > 0 && fails > 0) {
          setSnackBarProps('warning', `Generated ${success} stock checks. Failed generating ${fails} stock checks`);
        } else if (success > 0) {
          setSnackBarProps('success', `Generated ${success} stock checks`);
        } else {
          setSnackBarProps('warning',
            fails > 0
              ? `Failed generating ${fails} stock checks`
              : 'No stock checks were generated',
          );
        }
      })
      .catch((error) => {
        setSnackBarProps('error', t('errorMessage') + ' ' + error);
      })
      .finally(() => setIsGenerating(false));
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
    setHasChanged(true);
  };

  const handleSmartCountChange = () => {
    setIsSmartCount(!isSmartCount);
    setHasChanged(true);
  };

  const handleInputValue = (event: ChangeEvent<HTMLInputElement>) => {
    const newQuantity = parseInt(event.target.value, 10);
    if (!isNaN(newQuantity)) {
      setApprovalValue(newQuantity);
    } else {
      setApprovalValue(undefined);
    }
    setHasChanged(true);
  };

  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      const formattedTime = hour;
      options.push({
        value: formattedTime,
        label: `${String(hour).padStart(2, '0')}:00`,
      });
    }
    return options;
  };

  const handleSelectChange = (event: SelectChangeEvent<number>) => {
    setSelectedTime(event.target.value as number | undefined);
    setHasChanged(true);
  };

  const handleUserSelect = (event: SelectChangeEvent<string>) => {
    setSelectedUser(event.target.value);
    setHasChanged(true);
  };

  const handleLeaveClick = () => {
    navigate('/stock-count-schedule');
  };

  const handleValueChange = (itemNumber: string, newValue: string) => {
    let newNum = Number(newValue) || 0;
    newNum = newNum < 0 ? 0 : newNum;

    const updatedItems = itemsData.map((item) => {
      if (item.itemNumber === itemNumber) {
        return {
          ...item,
          toleranceValue: newNum,
        };
      }
      return item;
    });
    setItemsData(updatedItems);
    setHasChanged(true);
  };

  const handleQtyChange = (itemNumber: string, newValue: string) => {
    let newNum = parseFloat(newValue) || 0;
    newNum = newNum < 0 ? 0 : newNum;

    const updatedItems = itemsData.map((item) => {
      if (item.itemNumber === itemNumber) {
        return {
          ...item,
          tolerancePercentage: newNum,
        };
      }
      return item;
    });
    setItemsData(updatedItems);
    setHasChanged(true);
  };

  const handleCategoryChange = (id: number, newValue: string) => {
    const updatedItems = itemsData.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          category: newValue,
        };
      }
      return item;
    });
    setItemsData(updatedItems);
    setHasChanged(true);
  };

  const setSnackBarProps = (severity: 'error' | 'warning' | 'info' | 'success', message: string) => {
    severity === 'success' ? setSnackBarDuration(6000) : setSnackBarDuration(null);
    setIsSnackBarOpen(true);
    setSnackBarSeverity(severity);
    setSnackbarMessage(message);
  };

  const closeSnackBar = () => {
    setIsSnackBarOpen(false);
  };

  const closeDayOfMonthSnackBar = () => {
    setIsDayOfMonthSnackBarOpen(false);
  };

  const openDayOfMonthSnackBar = () => {
    setIsDayOfMonthSnackBarOpen(true);
  };
  return {
    t,
    isCategoriesModalOpen,
    isItemsModalOpen,
    isBulkModalOpen,
    isCategoryDeleteModalOpen,
    isDeleteModalOpen,
    allUsers,
    handleActiveChange,
    handleQtyChange,
    handleValueChange,
    handleLeaveClick,
    handleUserSelect,
    handleSelectChange,
    generateTimeOptions,
    handleInputValue,
    handleInputChange,
    submitStock,
    handleAddBulkItemClick,
    handleAddItemsClick,
    handleAddCategoriesClick,
    handleClose,
    handleitems,
    deleteItemRow,
    deleteCategoryRow,
    deleteItems,
    deleteCategories,
    handleCategories,
    handleSliderChange,
    handleIsTimeLockedChanged,
    name,
    today,
    selectedUser,
    approvalValue,
    selectedTime,
    isTimeLocked,
    sliderValue,
    categoryData,
    itemsData,
    closeSnackBar,
    isSnackBarOpen,
    snackBarSeverity,
    snackbarMessage,
    snackBarDuration,
    schedules,
    stores,
    setSchedules,
    deleteScheduleRow,
    isScheduleDeleteModalOpen,
    confirmDeleteSchedule,
    deactivateTarget,
    confirmDeactivate,
    isSaveConfirmOpen,
    handleSaveClick,
    confirmSave,
    handleScheduleActiveChange,
    handleSmartCountChange,
    isSmartCount,
    handleCategoryChange,
    isActive,
    isLoading,
    generateStock,
    isCreating,
    blocker,
    handleLeavePageModalClose,
    handleLeavePageModalConfirm,
    handleLeavePageModalCancel,
    currencySymbol,
    hasRightToEdit,
    closeDayOfMonthSnackBar,
    openDayOfMonthSnackBar,
    isDayOfMonthSnackBarOpen,
    isGenerating,
    isSubmitting,
    hasDuplicateSchedules,
    duplicateScheduleIndexes,
  };
};
